import { openDB, DBSchema, IDBPDatabase } from 'idb'

interface StoryDB extends DBSchema {
  stories: {
    key: string
    value: {
      id: string
      title: string
      content: string
      audioUrl?: string
      imageUrl?: string
      profileId: string
      createdAt: Date
      isFavorite: boolean
      rating?: number
      tags: string[]
      metadata?: Record<string, any>
      totalChapters: number
      completedChapters: number
      isCompleted: boolean
      lastReadAt?: Date
    }
  }
  profiles: {
    key: string
    value: {
      id: string
      name: string
      age: number
      interests: string[]
      sensitivities: string[]
      createdAt: Date
      avatarUrl?: string
      settings?: Record<string, any>
    }
  }
  offlineQueue: {
    key: string
    value: {
      id: string
      type: 'create' | 'update' | 'delete'
      endpoint: string
      data: any
      timestamp: Date
      retries: number
    }
  }
  media: {
    key: string
    value: {
      url: string
      blob: Blob
      type: 'image' | 'audio'
      size: number
      cachedAt: Date
    }
  }
}

class StoryDatabase {
  private db: IDBPDatabase<StoryDB> | null = null
  private readonly DB_NAME = 'BrAIghtKidsDB'
  private readonly DB_VERSION = 1

  async init(): Promise<void> {
    if (this.db) return

    this.db = await openDB<StoryDB>(this.DB_NAME, this.DB_VERSION, {
      upgrade(db) {
        // Stories store
        if (!db.objectStoreNames.contains('stories')) {
          const storyStore = db.createObjectStore('stories', { keyPath: 'id' })
          storyStore.createIndex('profileId', 'profileId')
          storyStore.createIndex('createdAt', 'createdAt')
          storyStore.createIndex('isFavorite', 'isFavorite')
        }

        // Profiles store
        if (!db.objectStoreNames.contains('profiles')) {
          const profileStore = db.createObjectStore('profiles', { keyPath: 'id' })
          profileStore.createIndex('createdAt', 'createdAt')
        }

        // Offline queue store
        if (!db.objectStoreNames.contains('offlineQueue')) {
          const queueStore = db.createObjectStore('offlineQueue', { keyPath: 'id' })
          queueStore.createIndex('timestamp', 'timestamp')
        }

        // Media cache store
        if (!db.objectStoreNames.contains('media')) {
          const mediaStore = db.createObjectStore('media', { keyPath: 'url' })
          mediaStore.createIndex('type', 'type')
          mediaStore.createIndex('cachedAt', 'cachedAt')
        }
      },
    })
  }

  private async ensureDB(): Promise<IDBPDatabase<StoryDB>> {
    if (!this.db) {
      await this.init()
    }
    return this.db!
  }

  // Stories operations
  async getStory(id: string) {
    const db = await this.ensureDB()
    return db.get('stories', id)
  }

  async getAllStories() {
    const db = await this.ensureDB()
    return db.getAll('stories')
  }

  async getStoriesByProfile(profileId: string) {
    const db = await this.ensureDB()
    return db.getAllFromIndex('stories', 'profileId', profileId)
  }

  async saveStory(story: StoryDB['stories']['value']) {
    const db = await this.ensureDB()
    return db.put('stories', story)
  }

  async deleteStory(id: string) {
    const db = await this.ensureDB()
    return db.delete('stories', id)
  }

  async getFavoriteStories() {
    const db = await this.ensureDB()
    return db.getAllFromIndex('stories', 'isFavorite', true)
  }

  // Profiles operations
  async getProfile(id: string) {
    const db = await this.ensureDB()
    return db.get('profiles', id)
  }

  async getAllProfiles() {
    const db = await this.ensureDB()
    return db.getAll('profiles')
  }

  async saveProfile(profile: StoryDB['profiles']['value']) {
    const db = await this.ensureDB()
    return db.put('profiles', profile)
  }

  async deleteProfile(id: string) {
    const db = await this.ensureDB()
    // Also delete all stories for this profile
    const stories = await this.getStoriesByProfile(id)
    const tx = db.transaction(['profiles', 'stories'], 'readwrite')
    
    await tx.objectStore('profiles').delete(id)
    for (const story of stories) {
      await tx.objectStore('stories').delete(story.id)
    }
    
    await tx.done
  }

  // Offline queue operations
  async addToQueue(item: Omit<StoryDB['offlineQueue']['value'], 'id'>) {
    const db = await this.ensureDB()
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    return db.put('offlineQueue', { ...item, id })
  }

  async getQueueItems() {
    const db = await this.ensureDB()
    return db.getAllFromIndex('offlineQueue', 'timestamp')
  }

  async removeFromQueue(id: string) {
    const db = await this.ensureDB()
    return db.delete('offlineQueue', id)
  }

  async updateQueueItem(id: string, updates: Partial<StoryDB['offlineQueue']['value']>) {
    const db = await this.ensureDB()
    const item = await db.get('offlineQueue', id)
    if (item) {
      return db.put('offlineQueue', { ...item, ...updates })
    }
  }

  // Media cache operations
  async cacheMedia(url: string, blob: Blob, type: 'image' | 'audio') {
    const db = await this.ensureDB()
    return db.put('media', {
      url,
      blob,
      type,
      size: blob.size,
      cachedAt: new Date(),
    })
  }

  async getCachedMedia(url: string) {
    const db = await this.ensureDB()
    return db.get('media', url)
  }

  async clearOldMedia(maxAge: number = 7 * 24 * 60 * 60 * 1000) {
    const db = await this.ensureDB()
    const cutoffDate = new Date(Date.now() - maxAge)
    const tx = db.transaction('media', 'readwrite')
    const index = tx.store.index('cachedAt')
    
    for await (const cursor of index.iterate()) {
      if (cursor.value.cachedAt < cutoffDate) {
        await cursor.delete()
      }
    }
  }

  async getStorageInfo() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate()
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0,
        percent: ((estimate.usage || 0) / (estimate.quota || 1)) * 100,
      }
    }
    return null
  }

  async clearAll() {
    const db = await this.ensureDB()
    const tx = db.transaction(db.objectStoreNames as any, 'readwrite')
    
    for (const storeName of db.objectStoreNames) {
      await tx.objectStore(storeName).clear()
    }
    
    await tx.done
  }
}

export const storyDB = new StoryDatabase()
export default storyDB